import * as React from 'react';

import {useFormGroupId, useFormGroupState} from '../FormGroup';
import {getDefinedValue, renderElement} from '../../utils';

import {
  TextareaContentView,
  TextareaPrefixSuffixView,
  TextareaPrefixWrapper,
  TextareaSuffixWrapper,
  TextareaView,
  TextareaWrapperView,
} from './Textarea.styles';

import type {SpringTooling} from '../__SharedTypes';

type TextareaSize = 'x-large' | 'medium';
export type TextareaProps = {
  /**
   * Allow row count to increase if text input overflows.
   */
  shouldAutoSize?: boolean;
  /**
   * The visible width.
   */
  cols?: number;
  /**
   * The default value when no value is provided. Used for uncontrolled `Textarea` components that do not have an `onChange` prop.
   */
  defaultValue?: string;
  /**
   * Renders the disabled state, which is visualized by being semi-transparent.
   */
  isDisabled?: boolean;
  /**
   * Renders the read-only state, which is visualized by being semi-transparent.
   */
  isReadOnly?: boolean;
  /**
   * Renders the error state, which is visualized by a red border.
   */
  isError?: boolean;
  /**
   * Indicates whether or not the field is required and is used to make Textarea more accessible to assistive technologies.
   */
  isRequired?: boolean;
  /**
   * The font family to use in the text area. Defaults to `'sansSerif'`.
   */
  fontFamily?: 'mono' | 'sansSerif';
  /**
   * The `id` attribute for the `Textarea`.
   */
  id?: string;
  /**
   * The `name` attribute for the `Textarea`.
   */
  name?: string;
  /**
   * Placeholder content when the `Textarea` is empty.
   */
  placeholder?: string;
  /**
   * Renders content before the input control.
   */
  prefix?: React.ReactNode | React.ComponentType;
  /**
   * Enables resizing by dragging.
   */
  resize?: 'vertical' | 'horizontal' | 'both' | 'none';
  /**
   * The number of visible text lines.
   */
  rows?: number;
  /**
   * Require the shift key to be held while pressing enter to trigger a new line.
   */
  requireShiftForNewLine?: boolean;
  /**
   * Renders a specific size style.
   */
  size?: TextareaSize;
  /**
   * Renders content after the input control.
   */
  suffix?: React.ReactNode | React.ComponentType;
  /**
   * The value of the form control. Used for controlled `Textarea` components that have an `onChange` prop.
   */
  value?: string;
} & React.InputHTMLAttributes<HTMLTextAreaElement> &
  SpringTooling;

/**
 * @component
 */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      shouldAutoSize,
      className,
      defaultValue,
      isDisabled,
      isReadOnly,
      isError: errorProp,
      isRequired: isRequiredProp,
      id: idProp,
      onBlur,
      onChange = () => undefined,
      onFocus,
      onKeyDown,
      prefix,
      resize = 'none',
      requireShiftForNewLine,
      style,
      suffix,
      value,
      rows = 4,
      ...rest
    }: TextareaProps,
    ref
  ) => {
    const id = useFormGroupId(idProp);
    const {
      ariaDescribedBy,
      isError: errorState,
      isRequired: isRequiredStateFromFormGroup,
    } = useFormGroupState();
    const [focused, setFocused] = React.useState(false);

    const isError = typeof errorProp === 'undefined' ? errorState : errorProp;
    const isRequired = getDefinedValue(
      isRequiredStateFromFormGroup,
      isRequiredProp
    );
    const innerRef: any = React.useRef<HTMLDivElement | null>(null);
    React.useImperativeHandle(ref, () => innerRef.current);

    const handleOnBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);
      onBlur && onBlur(event);
    };

    const handleOnFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(true);
      onFocus && onFocus(event);
    };

    const isReturnPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      return event.keyCode === 13;
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const safeKeyDown = (
        safeEvent: React.KeyboardEvent<HTMLTextAreaElement>
      ) => {
        if (onKeyDown) return onKeyDown(safeEvent);
      };
      if (requireShiftForNewLine) {
        if (isReturnPress(event) && !event.shiftKey) {
          event.preventDefault();
          safeKeyDown(event);
        } else if (!event.shiftKey) {
          safeKeyDown(event);
        }
        return false;
      }
      safeKeyDown(event);
    };

    const prefixMarkup = prefix ? (
      <TextareaPrefixSuffixView>
        {renderElement(prefix)}
      </TextareaPrefixSuffixView>
    ) : null;

    const suffixMarkup = suffix ? (
      <TextareaPrefixSuffixView>
        {renderElement(suffix)}
      </TextareaPrefixSuffixView>
    ) : null;

    React.useEffect(() => {
      if (!innerRef || !innerRef.current) return;
      // Allow toggling back to a static height
      if (!shouldAutoSize) {
        if (innerRef.current.style.height !== '') {
          // remove the height style
          innerRef.current.style.height = '';
        }
        return;
      }
      innerRef.current.style.height = '0px'; // reset height to 0px to get computed scrollHeight
      const scrollHeight = innerRef.current.scrollHeight;
      innerRef.current.style.height = scrollHeight + 'px'; // set the height to the computed scrollHeight
    }, [innerRef, value, shouldAutoSize]);

    return (
      <TextareaWrapperView
        className={className}
        data-disabled={isDisabled}
        data-error={isError}
        data-focused={focused}
        data-readonly={isReadOnly}
        style={style}
      >
        <TextareaContentView>
          {prefixMarkup ? (
            <TextareaPrefixWrapper>{prefixMarkup}</TextareaPrefixWrapper>
          ) : null}
          <TextareaView
            aria-describedby={ariaDescribedBy}
            defaultValue={defaultValue}
            disabled={isDisabled}
            id={id}
            onBlur={handleOnBlur}
            onChange={onChange}
            onFocus={handleOnFocus}
            readOnly={isReadOnly}
            ref={innerRef}
            required={isRequired}
            resize={!shouldAutoSize && resize ? resize : undefined}
            rows={rows}
            value={value}
            {...rest}
            onKeyDown={handleKeyDown}
          />
          {suffixMarkup ? (
            <TextareaSuffixWrapper>{suffixMarkup}</TextareaSuffixWrapper>
          ) : null}
        </TextareaContentView>
      </TextareaWrapperView>
    );
  }
);

Textarea.displayName = 'Textarea';
Textarea.toString = TextareaView.toString;

export default Textarea;
